import React, { useState } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  message,
  Checkbox,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  SendOutlined,
  PlusOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { invoiceService } from '../../services';
import { Invoice } from '../../types';
import { formatCurrency } from '../../utils/format';
import InvoiceExcelDetailModal from '../../components/InvoiceExcelDetailModal';
import './ExportInvoiceFromExcelPage.css';

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const ExportInvoiceFromExcelPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [exportingId, setExportingId] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    taxCode: '',
    customerName: '',
    phone: '',
    startDate: '',
    endDate: '',
  });

  const { data: invoiceResponse, isLoading } = useQuery({
    queryKey: ['invoices-excel', filters, currentPage, pageSize],
    queryFn: () =>
      invoiceService.getList({
        customerName: filters.customerName || undefined,
        taxCode: filters.taxCode || undefined,
        phone: filters.phone || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page: currentPage,
        pageSize,
      }),
  });

  const exportOneMutation = useMutation({
    mutationFn: (id: number) => invoiceService.exportInvoice(id),
    onMutate: (id) => setExportingId(id),
    onSuccess: (blob, id) => {
      downloadBlob(blob, `hoa-don-${id}.pdf`);
      message.success('Đang tải hóa đơn');
    },
    onError: () => message.error('Xuất hóa đơn thất bại'),
    onSettled: () => setExportingId(null),
  });

  const exportBulkMutation = useMutation({
    mutationFn: (payload: Parameters<typeof invoiceService.exportBulk>[0]) =>
      invoiceService.exportBulk(payload),
    onSuccess: (blob) => {
      downloadBlob(blob, `hoa-don-nhieu-${dayjs().format('YYYYMMDD-HHmm')}.zip`);
      message.success('Đang tải gói hóa đơn');
    },
    onError: () => message.error('Xuất hàng loạt thất bại'),
  });

  const onSearch = (values: Record<string, unknown>) => {
    setFilters({
      taxCode: (values.taxCode as string) || '',
      customerName: (values.customerName as string) || '',
      phone: (values.phone as string) || '',
      startDate: values.startDate ? (values.startDate as dayjs.Dayjs).format('YYYY-MM-DD') : '',
      endDate: values.endDate ? (values.endDate as dayjs.Dayjs).format('YYYY-MM-DD') : '',
    });
    setCurrentPage(1);
    setSelectedRowKeys([]);
  };

  const handleViewDetail = (id: number) => {
    setSelectedInvoiceId(id);
    setDetailModalVisible(true);
  };

  const goToImportExcel = () => navigate('/facility/invoices/from-excel/import');

  const handleExportOne = (id: number) => exportOneMutation.mutate(id);

  const handleExportAllMatching = () => {
    exportBulkMutation.mutate({
      exportAllMatching: true,
      filters: {
        taxCode: filters.taxCode || undefined,
        customerName: filters.customerName || undefined,
        phone: filters.phone || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      },
    });
  };

  /** Đồng bộ với backend: export_status hoặc sent_at / status */
  const isInvoiceExported = (record: Invoice): boolean => {
    const st = (record.export_status || '').toLowerCase();
    if (st.includes('đã') || st.includes('da xuat') || st.includes('exported')) return true;
    if (record.sent_at) return true;
    const statusLower = (record.status || '').toLowerCase();
    if (statusLower === 'exported' || statusLower === 'sent') return true;
    return false;
  };

  const formatNgayNhap = (record: Invoice) => {
    const raw = record.import_date || record.created_at;
    if (!raw) return '—';
    return dayjs(raw).format('DD/MM/YYYY');
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: 56,
      align: 'center' as const,
    },
    {
      title: 'Mã số thuế',
      dataIndex: 'customer_tax_code',
      key: 'customer_tax_code',
      width: 130,
      render: (text: string, record: Invoice) => (
        <Button
          type="link"
          className="excel-tax-link"
          onClick={() => handleViewDetail(record.id)}
        >
          {text || '—'}
        </Button>
      ),
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: 'Điện thoại',
      dataIndex: 'customer_phone',
      key: 'customer_phone',
      width: 120,
      align: 'center' as const,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: 'Dịch vụ',
      dataIndex: 'service_name',
      key: 'service_name',
      ellipsis: true,
    },
    {
      title: 'Giá tiền',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      align: 'right' as const,
      render: (amount: string) => formatCurrency(parseFloat(amount)),
    },
    {
      title: 'Ngày nhập',
      dataIndex: 'import_date',
      key: 'import_date',
      width: 120,
      align: 'center' as const,
      render: (_: unknown, record: Invoice) => formatNgayNhap(record),
    },
    {
      title: 'Xuất hóa đơn',
      key: 'export_invoice',
      width: 160,
      align: 'center' as const,
      render: (_: unknown, record: Invoice) => {
        if (isInvoiceExported(record)) {
          return (
            <Button
              type="link"
              className="excel-exported-label"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.id)}
            >
              Đã xuất HĐ
            </Button>
          );
        }
        return (
          <Button
            type="primary"
            className="btn-export-hd-green"
            icon={<SendOutlined />}
            loading={exportingId === record.id}
            onClick={() => handleExportOne(record.id)}
          >
            Xuất HĐ
          </Button>
        );
      },
    },
    {
      title: 'Tùy chọn',
      key: 'options',
      width: 100,
      align: 'center' as const,
      render: (_: unknown, record: Invoice) => {
        if (isInvoiceExported(record)) {
          return (
            <Space size="middle">
              <Button
                type="text"
                icon={<DownloadOutlined />}
                className="excel-icon-action"
                aria-label="Tải xuống"
                onClick={() => handleExportOne(record.id)}
              />
              <Button
                type="text"
                icon={<MailOutlined />}
                className="excel-icon-action"
                aria-label="Gửi email"
                onClick={() => message.info('Chức năng gửi email đang được hoàn thiện')}
              />
            </Space>
          );
        }
        return (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined />}
              className="excel-icon-action"
              aria-label="Sửa"
              onClick={() => handleViewDetail(record.id)}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label="Xóa"
              onClick={() => message.warning('Chức năng xóa cần xác nhận từ quản trị')}
            />
          </Space>
        );
      },
    },
  ];

  const invoices = invoiceResponse?.data || [];
  const tableData = invoices.map((invoice, index) => ({
    ...invoice,
    index: (currentPage - 1) * pageSize + index + 1,
    key: invoice.id,
  }));

  const pageRowKeys = tableData.map((r) => r.key as React.Key);
  const allPageSelected =
    pageRowKeys.length > 0 && pageRowKeys.every((k) => selectedRowKeys.includes(k));
  const somePageSelected =
    pageRowKeys.some((k) => selectedRowKeys.includes(k)) && !allPageSelected;

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    hideSelectAll: true,
  };

  const onFooterSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeys(pageRowKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  return (
    <div className="export-invoice-excel-page">
      <div className="page-toolbar">
        <h1 className="page-title">Quản lý hóa đơn</h1>
        <Space>
          <Button
            type="primary"
            size="large"
            onClick={goToImportExcel}
            className="btn-import-excel"
          >
            <PlusOutlined /> Import dữ liệu excel
          </Button>
        </Space>
      </div>

      <Form form={form} layout="inline" onFinish={onSearch} className="search-form">
        <Form.Item name="taxCode">
          <Input placeholder="Mã số thuế" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="customerName">
          <Input placeholder="Tên khách hàng" style={{ width: 200 }} allowClear />
        </Form.Item>
        <Form.Item name="phone">
          <Input placeholder="Điện thoại" style={{ width: 160 }} allowClear />
        </Form.Item>
        <Form.Item name="startDate">
          <DatePicker placeholder="Từ ngày" format="DD/MM/YYYY" locale={locale} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="endDate">
          <DatePicker placeholder="Đến ngày" format="DD/MM/YYYY" locale={locale} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
            Tìm kiếm
          </Button>
        </Form.Item>
      </Form>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={tableData}
        loading={isLoading}
        scroll={{ x: 1280 }}
        footer={() => (
          <div className="excel-table-footer">
            <Checkbox
              checked={allPageSelected}
              indeterminate={somePageSelected}
              onChange={(e) => onFooterSelectAll(e.target.checked)}
            >
              Chọn tất cả
            </Checkbox>
            <Button
              type="primary"
              className="btn-bulk-export-all"
              icon={<SendOutlined />}
              loading={exportBulkMutation.isPending}
              onClick={handleExportAllMatching}
            >
              Xuất toàn bộ Hóa đơn
            </Button>
          </div>
        )}
        pagination={{
          current: currentPage,
          pageSize,
          total: invoiceResponse?.meta?.count || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Tổng ${total} hóa đơn`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 10);
          },
        }}
      />

      <InvoiceExcelDetailModal
        visible={detailModalVisible}
        invoiceId={selectedInvoiceId}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedInvoiceId(null);
        }}
      />
    </div>
  );
};

export default ExportInvoiceFromExcelPage;
